import React from 'react';

import { didRequestFinish } from '@lumapps/base-api/utils/requests';

import { getInitService, type InitServiceOptions } from '../api';
import { onInitialStateLoaded, type Callbacks } from '../utils/onInitialStateLoaded';

declare const window: any;

export interface UseInitialStateOptions extends Partial<InitServiceOptions> {
    callbacks?: Callbacks;
    shouldFetchInit?: boolean;
    defaultInitState?: any;
}

/**
 * Hook to handle loading the initial state, extracted from useInitialState.
 */
const useInitialStateLoader = (
    setInitialState: React.Dispatch<React.SetStateAction<Record<string, any> | undefined>>,
    options: UseInitialStateOptions,
) => {
    React.useEffect(() => {
        const { shouldFetchInit = true } = options;

        if (shouldFetchInit) {
            const onStateLoaded = () => onInitialStateLoaded(setInitialState, false, options.callbacks);

            if (window.initRequest) {
                if (didRequestFinish(window.initRequest)) {
                    onStateLoaded();
                } else {
                    window.initRequest.addEventListener('load', onStateLoaded);
                }
            } else {
                getInitService({
                    ...options,
                    onInitialStateLoaded: onStateLoaded,
                });
            }
        } else {
            setInitialState(options.defaultInitState);
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);
};

/**
 * This hook adds the different listeners in order to retrieve the information from the
 * initial request. If the request has finished, we trigger the initial state loaded, and if it has not finished,
 * we add a listener to the request and trigger the onLoad when it is the right time.
 */
export const useInitialState = (options: UseInitialStateOptions = {}) => {
    const [initialState, setInitialState] = React.useState<Record<string, any> | undefined>();

    useInitialStateLoader(setInitialState, options);

    return initialState || null;
};

/** Similar to `useInitialState` while adding a retry mechanism */
export const useInitialStateWithRetry = (options: UseInitialStateOptions = {}) => {
    const [initialState, setInitialState] = React.useState<Record<string, any> | undefined>();

    useInitialStateLoader(setInitialState, options);

    return {
        initialState: initialState || null,
        onRetryInitialState: () => {
            getInitService({
                ...options,
                onInitialStateLoaded: () => onInitialStateLoaded(setInitialState, false, options.callbacks),
            });
        },
    };
};
